import React from 'react'
import { useRef, useMemo, useScriptsLoader } from 'sb-hooks'
import EventAggregator from 'sb-events'
import config from 'app-config'

import Text from 'components/_new/typography/Text/Text'
import { ContentLoader } from 'components/_new/ui'


type PayPalToken = { type: string, id: string }

export type Recurly = {
  formRef: React.MutableRefObject<HTMLFormElement>
  events: EventAggregator
  elements: RecurlyNS.ElementsInstance
  token: () => Promise<ReturnType<RecurlyNS.TokenHandler>>
  payPalToken: ({ displayText }) => Promise<PayPalToken>
  applePayToken: ({ label, total }) => Promise<PayPalToken>
  on: (event: 'submit', handler: any) => void
  off: (event: 'submit', handler: any) => void
  dispatchEvent: (event: string, ...args: any[]) => void
}

type Result = {
  isFetching: boolean
  isLoaded: boolean
  recurly: Recurly
  error: Error
}

const useRecurlyLoader = (): Result => {
  const { isFetching, isLoaded, error } = useScriptsLoader([ 'https://js.recurly.com/v4/recurly.js' ])
  const formRef = useRef<HTMLFormElement>(null)

  const recurly = useMemo(() => {
    if (!isLoaded) {
      return
    }

    window.recurly.configure({
      publicKey: config.publicKeys.recurlyPublicKey,
      fields: {
        all: {
          style: {
            fontFamily: 'Open Sans',
            fontSize: '16px',
            fontColor: '#000000',
            fontSmoothing: 'antialiased',
            fontWeight: 'normal',
            lineHeight: '40px',
            placeholder: {
              fontSize: '16px',
              color: '#959ba6 !important',
              fontWeight: '600',
            },
          },
        },
      },
    })

    const recurly: Recurly = {
      formRef,
      events: null,
      elements: null,
      token: null,
      payPalToken: null,
      applePayToken: null,
      on: null,
      off: null,
      dispatchEvent: null,
    }

    recurly.elements = window.recurly.Elements()

    recurly.token = () => (
      new Promise((resolve, reject) => {
        window.recurly.token(recurly.elements, formRef.current, (err, token) => {
          if (err) {
            reject(err)
          }
          else {
            resolve(token)
          }
        })
      })
    )

    recurly.payPalToken = ({ displayText }) => (
      new Promise((resolve, reject) => {
        const payPal = window.recurly.PayPal({
          display: {
            displayName: `Payment for Scentbird ${displayText}`,
          },
        })

        payPal.on('token', (token: PayPalToken) => {
          console.log('PayPal Token:', token)
          resolve(token)
        })

        payPal.on('error', (err) => {
          console.log('PayPal Error:', err)
          reject(err)
        })

        // @ts-ignore
        payPal.on('cancel', () => {
          console.log('PayPal Canceled')
          reject(new Error('Canceled'))
        })

        payPal.start()
      })
    )

    let applePay

    window.recurly.ready(() => {
      applePay = window.recurly.ApplePay({
        country: 'US',
        currency: 'USD',
        label: 'My Subscription',
        total: '29.00',
        // ...config,
      })

      applePay.ready(() => {
        // TODO recurly. "ready" never invokes! No idea why.. there are no errors - added on 7/21/20 by pavelivanov
        events.dispatch('apple-pay ready')
      })

      applePay.on('error', (err) => {
        console.error('ApplePay error', err)
      })
    })

    recurly.applePayToken = () => (
      new Promise((resolve, reject) => {
        applePay.on('token', (token) => {
          resolve(token)
        })

        applePay.on('error', (err) => {
          console.error('ApplePay error', err)
          reject(err)
        })
      })
    )

    const events = new EventAggregator()

    recurly.on = (event, handler) => {
      events.subscribe(event, handler)
    }

    recurly.off = (event, handler) => {
      events.unsubscribe(event, handler)
    }

    recurly.dispatchEvent = (event, ...args) => {
      events.dispatch(event, ...args)
    }

    return recurly
  }, [ isLoaded ])

  return {
    isFetching,
    isLoaded,
    recurly,
    error,
  }
}

export const Context = React.createContext<Recurly>(null)

const RecurlyProvider = ({ children }) => {
  const { isFetching, recurly, error } = useRecurlyLoader()

  if (isFetching) {
    return <ContentLoader />
  }

  if (error || !recurly) {
    return (
      <Text size="18-24" color="error" message="Error on loading Recurly!" />
    )
  }

  return (
    <Context.Provider value={recurly}>
      {children}
    </Context.Provider>
  )
}


export default RecurlyProvider
